import { createSelector } from '@reduxjs/toolkit';

import isEqual from 'lodash-es/isEqual';

import type { RootState } from 'src/store';
import { WidgetDataState } from '../types/scenarioAnalysis.types';
import { normalizeWidgets } from '../utils/scenarioAnalysisUtils';

// Base selectors
const getScenarioAnalysisState = (state: RootState) => state.scenarioAnalysis;
const getWidgetsData = (state: RootState) => state.scenarioAnalysis.widgets.data;
const getWidgetLayouts = (state: RootState) => state.scenarioAnalysis.widgetLayouts;
const getSavedWidgets = (state: RootState) => state.scenarioAnalysis.savedWidgets;

// Memoized selectors
export const getWidgetsState = createSelector([getScenarioAnalysisState], (state) => state.widgets);

export const getLastRefreshTimeState = createSelector(
  [getScenarioAnalysisState],
  (state) => state.lastRefreshTime,
);

export const getWidgetById = createSelector(
  [getWidgetsData, (_state, widgetId: string) => widgetId],
  (widgets, widgetId) => widgets.find((w) => w.id === widgetId),
);

export const getWidgetLayout = createSelector(
  [getWidgetLayouts, (_state, widgetId: string) => widgetId],
  (layouts, widgetId) => layouts[widgetId],
);

export const getWidgetWithLayout = createSelector(
  [
    (state: RootState, widgetId: string) => getWidgetById(state, widgetId),
    (state: RootState, widgetId: string) => getWidgetLayout(state, widgetId),
  ],
  (widget, layout) => (widget && layout ? { ...widget, ...layout } : null),
);

export const getAllWidgetsWithLayouts = createSelector(
  [getWidgetsData, getWidgetLayouts],
  (widgets, layouts) =>
    widgets.map((widget) => {
      const layout = layouts[widget.id];
      return layout ? { ...widget, ...layout } : widget;
    }),
);

/**
 * Derived dirtiness: the widgets on screen vs the last persisted snapshot.
 * Pure, so no effect/ref is needed to keep an "unsaved changes" flag in sync.
 */
export const selectHasUnsavedChanges = createSelector(
  [getAllWidgetsWithLayouts, getSavedWidgets],
  (current, saved) => !isEqual(normalizeWidgets(current), saved),
);

const DEFAULT_WIDGET_DATA_STATE: WidgetDataState = {
  loading: false,
  error: null,
  rawData: [],
  chartData: [],
  seriesKeys: [],
  seriesUoms: {},
};

export const getWidgetDataState = (state: RootState, widgetId: string): WidgetDataState =>
  state.scenarioAnalysis.widgetData[widgetId] ?? DEFAULT_WIDGET_DATA_STATE;
